import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Globe, Loader2, Navigation, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCountry } from "@/contexts/CountryContext";

const STORAGE_KEY = "recomendalibros:postal_code";

export default function NearbyBookstores() {
    const { t } = useTranslation();
    const { country } = useCountry();
    const [postalCode, setPostalCode] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submittedPC, setSubmittedPC] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);

    // Restore last entered postal code so users don't have to retype on every visit.
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) setPostalCode(stored);
        } catch { /* ignore */ }
    }, []);

    const fetchStores = async (pc, limit = 3) => {
        const isExpanded = limit > 3;
        if (isExpanded) setLoadingAll(true); else setLoading(true);
        try {
            // Expanded view asks for the backend's hard cap (10) — matches the
            // Query validation in /api/bookstores/nearby. Merged CEGAL + OSM
            // will typically have dozens within radius; we show the 10 closest.
            const { data } = await api.get("/bookstores/nearby", {
                params: { postal_code: pc, country, limit },
            });
            setData(data);
            setSubmittedPC(pc);
            if (isExpanded) setShowAll(true);
            try { localStorage.setItem(STORAGE_KEY, pc); } catch { /* ignore */ }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || t("nearby.error"));
            setData(null);
        } finally {
            setLoading(false);
            setLoadingAll(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const pc = postalCode.trim();
        if (pc.length < 3) {
            toast.error(t("nearby.invalid_postal"));
            return;
        }
        setShowAll(false);
        fetchStores(pc);
    };

    const handleShowAll = () => {
        if (submittedPC) fetchStores(submittedPC, 10);
    };

    return (
        <section className="border border-[var(--border)] bg-[var(--bg-elevated)] p-6 md:p-8 mt-12" data-testid="nearby-bookstores">
            <div className="flex items-center gap-3 mb-3">
                <MapPin size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
                    {t("nearby.eyebrow")}
                </p>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[var(--ink)] mb-2"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}>
                {t("nearby.title")}
            </h3>
            <p className="text-sm text-[var(--ink-muted)] mb-6 max-w-2xl">{t("nearby.lead")}</p>

            <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3 mb-8">
                <div className="flex items-center gap-2 border-b border-[var(--ink)] pb-1 flex-1 min-w-[200px] max-w-md">
                    <Search size={16} strokeWidth={1.5} className="text-[var(--ink-dim)]" />
                    <input
                        data-testid="nearby-postal-input"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder={t("nearby.postal_placeholder")}
                        maxLength={12}
                        className="flex-1 bg-transparent border-0 outline-none text-base text-[var(--ink)] py-2"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="nearby-submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-5 py-2 text-xs uppercase tracking-widest"
                >
                    {loading ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} strokeWidth={1.8} />}
                    {t("nearby.search_btn")}
                </button>
            </form>

            {data && data.bookstores?.length === 0 && (
                <p className="text-sm text-[var(--ink-dim)] italic">
                    {t("nearby.empty", { pc: submittedPC })}
                </p>
            )}

            {data && data.bookstores?.length > 0 && (
                <div data-testid="nearby-results">
                    <p className="text-xs text-[var(--ink-dim)] mb-4">
                        {t("nearby.found", { n: data.bookstores.length, pc: submittedPC })}
                    </p>
                    <ol className="space-y-4">
                        {data.bookstores.map((s, i) => (
                            <li
                                key={s.id}
                                data-testid={`bookstore-${i}`}
                                className="flex items-start gap-4 border-l-2 border-[var(--accent)] pl-4 py-1"
                            >
                                <span className="font-mono-num text-xs text-[var(--accent)] mt-1.5">
                                    {(i + 1).toString().padStart(2, "0")}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="font-display text-lg text-[var(--ink)] leading-tight"
                                            style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                                            {s.name}
                                        </h4>
                                        {s.source === "cegal" && (
                                            <span
                                                data-testid={`bookstore-cegal-badge-${i}`}
                                                title={t("nearby.cegal_tooltip")}
                                                className="text-[9px] uppercase tracking-[0.18em] text-[var(--accent)] border border-[var(--accent)]/40 px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                                            >
                                                {t("nearby.cegal_badge")}
                                            </span>
                                        )}
                                    </div>
                                    {s.address && (
                                        <p className="text-sm text-[var(--ink-muted)]">{s.address}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[var(--ink-dim)]">
                                        <span className="inline-flex items-center gap-1">
                                            <Navigation size={11} strokeWidth={1.5} />
                                            {s.distance_km} km
                                        </span>
                                        {s.phone && (
                                            <a
                                                href={`tel:${s.phone}`}
                                                className="inline-flex items-center gap-1 hover:text-[var(--accent)] transition-colors"
                                            >
                                                <Phone size={11} strokeWidth={1.5} />
                                                {s.phone}
                                            </a>
                                        )}
                                        {s.website && (
                                            <a
                                                href={s.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 hover:text-[var(--accent)] transition-colors"
                                            >
                                                <Globe size={11} strokeWidth={1.5} />
                                                {t("nearby.website")}
                                            </a>
                                        )}
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.name + (s.address ? ", " + s.address : ""))}&query_place_id=`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline uppercase tracking-widest"
                                        >
                                            {t("nearby.directions")}
                                        </a>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>

                    {/* Show "View all" only when we're still in the condensed
                        3-item view AND the backend reported more nearby shops
                        than we currently display. After expansion, hide the
                        CTA since we already show the backend's hard cap. */}
                    {!showAll && data.total_found > data.bookstores.length && (
                        <div className="mt-6 flex justify-center">
                            <button
                                type="button"
                                data-testid="show-all-bookstores"
                                onClick={handleShowAll}
                                disabled={loadingAll}
                                className="inline-flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-widest border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded-full transition-all disabled:opacity-50"
                            >
                                {loadingAll ? (
                                    <Loader2 size={12} strokeWidth={1.8} className="animate-spin" />
                                ) : (
                                    <Search size={12} strokeWidth={1.8} />
                                )}
                                {t("nearby.view_all", { n: data.total_found })}
                            </button>
                        </div>
                    )}

                    <p className="text-[10px] text-[var(--ink-dim)] mt-6 italic">{t("nearby.attribution")}</p>
                </div>
            )}
        </section>
    );
}
